import { useEffect, useState, type FormEvent } from 'react'
import { X } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { HUMAN_DEPARTMENTS, type Department } from '@/lib/departments'
import {
  CreateDepartmentModal,
  type DirectoryDepartment,
  type DirectoryScope,
} from './CreateDepartmentModal'

/** Shape returned by GET/POST `/artists/:id/email-directory[/people]`. */
export interface DirectoryPerson {
  id: string
  scope: DirectoryScope
  name: string
  email: string
  title: string | null
  departmentId: string | null
  isHead: boolean
  role: string | null
  orgRole: string | null
  departments: Department[]
}

export interface CreatePersonModalProps {
  open: boolean
  onClose: () => void
  onCreated: (result: DirectoryPerson) => void
  defaultScope?: DirectoryScope
  /** Existing directory departments — used to populate the artist-scope picker. */
  departments?: DirectoryDepartment[]
}

/**
 * Small centred modal for adding a person (artist team member, or linking an
 * existing org roster user to a department) from inside the email routing
 * flow designer. Standalone — Task 6 wires it into DirectoryPanel /
 * RuleEditorSheet via `open` + `onCreated`.
 */
export function CreatePersonModal({
  open,
  onClose,
  onCreated,
  defaultScope = 'artist',
  departments = [],
}: CreatePersonModalProps) {
  const [scope, setScope] = useState<DirectoryScope>(defaultScope)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [title, setTitle] = useState('')
  const [departmentId, setDepartmentId] = useState('')
  const [orgDepartment, setOrgDepartment] = useState<Department | ''>('')
  const [isHead, setIsHead] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [localDepartments, setLocalDepartments] = useState(departments)
  const [showCreateDept, setShowCreateDept] = useState(false)

  useEffect(() => {
    if (!open) return
    setScope(defaultScope)
    setName('')
    setEmail('')
    setTitle('')
    setDepartmentId('')
    setOrgDepartment('')
    setIsHead(false)
    setError(null)
    setSubmitting(false)
    setLocalDepartments(departments)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultScope])

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      // Capture phase + stopImmediatePropagation so this doesn't also bubble
      // up and close a parent RuleEditorSheet/DirectoryPanel this modal is
      // stacked on top of.
      e.stopImmediatePropagation()
      if (showCreateDept) setShowCreateDept(false)
      else onClose()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open, onClose, showCreateDept])

  if (!open) return null

  const artistDepartments = localDepartments.filter((d) => d.scope === 'artist')

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)

    if (!name.trim()) {
      setError('Name is required')
      return
    }
    if (!email.trim()) {
      setError('Email is required')
      return
    }

    setSubmitting(true)
    try {
      const body: Record<string, unknown> = {
        scope,
        name: name.trim(),
        email: email.trim(),
      }
      if (scope === 'artist') {
        body.title = title.trim() || undefined
        body.departmentId = departmentId || undefined
        body.isHead = isHead
      } else {
        body.mapsToOrgDepartment = orgDepartment || undefined
      }
      const result = await artistFetch<DirectoryPerson>('/email-directory/people', {
        method: 'POST',
        body,
      })
      onCreated(result)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Failed to add person')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-ovation-ink/30 backdrop-blur-[2px]"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="relative flex w-full max-w-sm flex-col rounded-xl border border-ovation-ink/10 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-ovation-ink/10 px-5 py-4">
          <h2 className="text-base font-semibold text-ovation-ink">Add person</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-ovation-muted hover:bg-ovation-ink/5 hover:text-ovation-ink"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col">
          <div className="space-y-4 px-5 py-4">
            <fieldset className="flex gap-2">
              <legend className="mb-1 block text-xs font-medium uppercase tracking-wider text-ovation-muted">
                Scope
              </legend>
              {(
                [
                  ['artist', 'This artist'],
                  ['organisation', 'Whole organisation'],
                ] as const
              ).map(([value, label]) => (
                <label
                  key={value}
                  className={
                    'flex flex-1 cursor-pointer items-center justify-center rounded-md border px-2 py-1.5 text-xs transition-colors ' +
                    (scope === value
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-800'
                      : 'border-ovation-ink/10 hover:bg-ovation-ink/5')
                  }
                >
                  <input
                    type="radio"
                    name="person-scope"
                    value={value}
                    checked={scope === value}
                    onChange={() => setScope(value)}
                    className="sr-only"
                  />
                  {label}
                </label>
              ))}
            </fieldset>

            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-ovation-muted">
                Name
              </label>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Jordan Reyes"
                className="w-full rounded-lg border border-ovation-ink/15 px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-ovation-muted">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="person@example.com"
                className="w-full rounded-lg border border-ovation-ink/15 px-3 py-2 text-sm"
              />
              {scope === 'organisation' && (
                <p className="mt-1 text-xs text-ovation-muted">
                  Must already have an Ovation account on this organisation's roster — invite via
                  Roster first if needed.
                </p>
              )}
            </div>

            {scope === 'artist' && (
              <>
                <div>
                  <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-ovation-muted">
                    Title (optional)
                  </label>
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Tour Manager"
                    className="w-full rounded-lg border border-ovation-ink/15 px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-ovation-muted">
                    Department (optional)
                  </label>
                  <select
                    value={departmentId}
                    onChange={(e) => setDepartmentId(e.target.value)}
                    className="w-full rounded-lg border border-ovation-ink/15 px-3 py-2 text-sm"
                  >
                    <option value="">No department</option>
                    {artistDepartments.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowCreateDept(true)}
                    className="mt-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
                  >
                    + Create department
                  </button>
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={isHead}
                    onChange={(e) => setIsHead(e.target.checked)}
                    className="rounded border-ovation-ink/20"
                  />
                  Department head (first in cascade)
                </label>
              </>
            )}

            {scope === 'organisation' && (
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-ovation-muted">
                  Department (optional)
                </label>
                <select
                  value={orgDepartment}
                  onChange={(e) => setOrgDepartment((e.target.value || '') as Department | '')}
                  className="w-full rounded-lg border border-ovation-ink/15 px-3 py-2 text-sm"
                >
                  <option value="">No department</option>
                  {HUMAN_DEPARTMENTS.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
          </div>

          <div className="flex gap-2 border-t border-ovation-ink/10 px-5 py-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border border-ovation-ink/15 px-4 py-2 text-sm font-medium hover:bg-ovation-ink/5"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 rounded-lg bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
            >
              {submitting ? 'Adding…' : 'Add person'}
            </button>
          </div>
        </form>
      </div>

      {showCreateDept && (
        <CreateDepartmentModal
          open={showCreateDept}
          defaultScope="artist"
          onClose={() => setShowCreateDept(false)}
          onCreated={(dept) => {
            setLocalDepartments((prev) => [...prev, dept])
            setDepartmentId(dept.id)
            setShowCreateDept(false)
          }}
        />
      )}
    </div>
  )
}
